/**
 * Parent Dashboard — My Registrations
 *
 * No Firebase Auth required. Session is stored in sessionStorage as
 * { cardCode, parentEmail } and verified on each mount via lookupStudentForParent.
 *
 * Status display:
 *   paid / completed            → verde "Activo"
 *   awaiting_payment / declined → amarillo "Pago pendiente"
 *   awaiting_manual_payment     → azul "Pago en escuela"
 *   cancelled                   → gris "Cancelado"
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { lookupStudentForParent, getClassesForForum } from "../lib/api";
import type { MyRegistrationSummary, ForumClassFromApi, ActivePeriod } from "../types";

const BRAND_NAVY = "#161b3d";
const BRAND_MAROON = "#8b1e22";
const LANG_STORAGE_KEY = "forumLang";
const SESSION_KEY = "parentSession";

function useLang(): "es" | "en" {
  try {
    const s = localStorage.getItem(LANG_STORAGE_KEY);
    return s === "en" ? "en" : "es";
  } catch (_) {
    return "es";
  }
}

interface ParentSession {
  cardCode: string;
  parentEmail: string;
}

function readSession(): ParentSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed.cardCode === "string" && typeof parsed.parentEmail === "string") {
      return { cardCode: parsed.cardCode, parentEmail: parsed.parentEmail };
    }
    return null;
  } catch (_) {
    return null;
  }
}

const MONTH_NAMES_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const MONTH_NAMES_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function formatMonth(yyyyMm: string, lang: "es" | "en" = "es"): string {
  if (!yyyyMm) return "";
  const [y, m] = yyyyMm.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return yyyyMm;
  return `${(lang === "en" ? MONTH_NAMES_EN : MONTH_NAMES_ES)[m - 1]} ${y}`;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("es-DO", {
    style: "currency",
    currency: "DOP",
    maximumFractionDigits: 0,
  }).format(amount);
}

function classScheduleLine(cls: ForumClassFromApi, lang: "es" | "en"): string {
  const day = lang === "en" ? (cls.scheduleDayEn ?? cls.scheduleDay) : cls.scheduleDay;
  const fromAdmin = [day, cls.scheduleTime].filter(Boolean).join(" · ");
  const fromLegacy = [
    (lang === "es" ? cls.scheduleEs : cls.scheduleEn) ?? cls.scheduleEs ?? cls.scheduleEn ?? "",
    cls.time,
  ]
    .filter(Boolean)
    .join(" · ");
  return fromAdmin || fromLegacy;
}

interface StatusBadgeProps {
  status: string;
  lang: "es" | "en";
}

function StatusBadge({ status, lang }: StatusBadgeProps) {
  if (status === "paid" || status === "completed") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-green-50 text-green-700 border border-green-100">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
        {lang === "es" ? "Activo" : "Active"}
      </span>
    );
  }
  if (status === "awaiting_payment" || status === "declined") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-yellow-50 text-yellow-700 border border-yellow-100">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 inline-block" />
        {lang === "es" ? "Pago pendiente" : "Payment pending"}
      </span>
    );
  }
  if (status === "awaiting_manual_payment") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-100">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
        {lang === "es" ? "Pago en escuela" : "School payment"}
      </span>
    );
  }
  if (status === "cancelled") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-50 text-gray-500 border border-gray-200">
        {lang === "es" ? "Cancelado" : "Cancelled"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500">
      {status}
    </span>
  );
}

function canPay(status: string): boolean {
  return status === "awaiting_payment" || status === "awaiting_manual_payment" || status === "declined";
}

// ── Inline class list ─────────────────────────────────────────────────────────

interface ClassListProps {
  classIds: string[];
  classMap: Map<string, ForumClassFromApi>;
  paymentFrequency: string;
  lang: "es" | "en";
}

function ClassList({ classIds, classMap, paymentFrequency, lang }: ClassListProps) {
  if (classIds.length === 0) return null;

  const freqLabel =
    paymentFrequency === "CUATRIMESTRAL"
      ? lang === "es" ? "Cuatrimestral" : "4-month"
      : lang === "es" ? "Bimestral" : "2-month";

  return (
    <div className="mt-3 pt-3 border-t border-black/[0.06] space-y-2">
      {classIds.map((id) => {
        const cls = classMap.get(id);
        const name = cls
          ? ((lang === "es" ? cls.nameEs ?? cls.name : cls.nameEn ?? cls.name) || cls.name)
          : id;
        const schedule = cls ? classScheduleLine(cls, lang) : "";
        return (
          <div key={id} className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[13px] font-semibold text-[#1d1d1f] leading-snug">{name}</p>
              {schedule && (
                <p className="text-[11px] text-[#86868b] uppercase tracking-wide">{schedule}</p>
              )}
            </div>
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#6e6e73] bg-gray-100 px-1.5 py-0.5 rounded-md whitespace-nowrap">
              {freqLabel}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ParentDashboard() {
  const navigate = useNavigate();
  const lang = useLang();

  const T = {
    myEnrollments: lang === "es" ? "Mis inscripciones" : "My enrollments",
    signOut: lang === "es" ? "Salir" : "Sign out",
    logoutTitle: lang === "es" ? "¿Cerrar sesión?" : "Sign out?",
    logoutBody:
      lang === "es"
        ? "Tendrás que ingresar tu código y correo de nuevo para ver tus inscripciones."
        : "You'll need to enter your code and email again to view your enrollments.",
    logoutConfirm: lang === "es" ? "Cerrar sesión" : "Sign out",
    cancel: lang === "es" ? "Cancelar" : "Cancel",
    loadError: lang === "es" ? "No se pudo cargar la información." : "Could not load information.",
    emptyTitle: lang === "es" ? "No hay inscripciones aún." : "No enrollments yet.",
    emptyBody:
      lang === "es"
        ? "Tus inscripciones aparecerán aquí una vez que te registres."
        : "Your enrollments will appear here once you register.",
    goToForm: lang === "es" ? "Ir al formulario de inscripción" : "Go to registration form",
    total: lang === "es" ? "Total:" : "Total:",
    viewAndPay: lang === "es" ? "Ver detalles y pagar" : "View details and pay",
    payAtSchool:
      lang === "es" ? "Completa tu pago en la escuela." : "Complete your payment at the school.",
    newEnrollment: lang === "es" ? "+ Nueva inscripción" : "+ New enrollment",
    student: lang === "es" ? "Estudiante" : "Student",
  };

  const session = readSession();

  const [registrations, setRegistrations] = useState<MyRegistrationSummary[]>([]);
  const [activePeriods, setActivePeriods] = useState<ActivePeriod[]>([]);
  const [studentName, setStudentName] = useState("");
  const [cardCode, setCardCode] = useState("");
  const [classMap, setClassMap] = useState<Map<string, ForumClassFromApi>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  useEffect(() => {
    if (!session) {
      navigate("/parent/login", { replace: true });
      return;
    }
    let cancelled = false;
    setLoading(true);

    Promise.all([
      lookupStudentForParent(session.cardCode, session.parentEmail),
      getClassesForForum(),
    ])
      .then(([studentRes, classesRes]) => {
        if (cancelled) return;
        setStudentName(studentRes.studentName);
        setCardCode(studentRes.cardCode);
        setRegistrations(studentRes.registrations);
        setActivePeriods(studentRes.activePeriods);
        setClassMap(new Map(classesRes.classes.map((c) => [c.id, c])));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : T.loadError;
        if (msg.includes("not-found") || msg.includes("No encontramos")) {
          sessionStorage.removeItem(SESSION_KEY);
          navigate("/parent/login", { replace: true });
        } else {
          setError(T.loadError);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sorted unique months (newest first) for the filter pills.
  const months = useMemo(() => {
    const seen = new Set<string>();
    for (const r of registrations) if (r.month) seen.add(r.month);
    return [...seen].sort((a, b) => (a > b ? -1 : 1));
  }, [registrations]);

  const visibleRegistrations = useMemo(
    () => (selectedMonth ? registrations.filter((r) => r.month === selectedMonth) : registrations),
    [registrations, selectedMonth]
  );

  // Compute blocked classes: those whose paid period hasn't expired yet.
  const blockedClasses = useMemo(() => {
    const todayMonth = new Date().toISOString().slice(0, 7); // "2026-05"
    return activePeriods
      .filter((p) => p.reEnrollMonth > todayMonth)
      .map((p) => ({ classId: p.classId, reEnrollMonth: p.reEnrollMonth }));
  }, [activePeriods]);

  const handleNewEnrollment = () => {
    navigate("/", {
      state: {
        prefillCardCode: cardCode || session?.cardCode,
        prefillStudentName: studentName,
        blockedClasses,
      },
    });
  };

  const handleSignOut = () => {
    sessionStorage.removeItem(SESSION_KEY);
    navigate("/parent/login", { replace: true });
  };

  return (
    <motion.div
      className="min-h-screen bg-[#f5f5f7] font-sans"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Logout confirmation modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowLogoutConfirm(false)}
          >
            <motion.div
              className="w-full max-w-xs bg-white rounded-3xl shadow-2xl p-6"
              initial={{ opacity: 0, scale: 0.94, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 10 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-[17px] font-bold text-[#1d1d1f] mb-1">{T.logoutTitle}</h2>
              <p className="text-[13px] text-[#6e6e73] mb-5">{T.logoutBody}</p>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full py-3 rounded-xl text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: BRAND_MAROON }}
                >
                  {T.logoutConfirm}
                </button>
                <button
                  type="button"
                  onClick={() => setShowLogoutConfirm(false)}
                  className="w-full py-3 rounded-xl text-[14px] font-semibold text-[#1d1d1f] bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  {T.cancel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-white border-b border-black/[0.06] px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl overflow-hidden shrink-0"
              style={{ backgroundColor: BRAND_NAVY }}
            >
              <img src="/logo/logo.png" alt="American School" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-[#1d1d1f]">{T.myEnrollments}</p>
              {(studentName || session?.cardCode) && (
                <p className="text-[11px] text-[#86868b]">{studentName || session?.cardCode}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowLogoutConfirm(true)}
            className="text-[12px] font-semibold text-[#8b1e22] hover:underline"
          >
            {T.signOut}
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Month filter pills — shown once data is loaded and there's more than one month */}
        {!loading && !error && months.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-4">
            <button
              type="button"
              onClick={() => setSelectedMonth(null)}
              className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
                selectedMonth === null
                  ? "text-white"
                  : "bg-white border border-black/[0.08] text-[#6e6e73] hover:border-[#161b3d]/30"
              }`}
              style={selectedMonth === null ? { backgroundColor: BRAND_NAVY } : undefined}
            >
              {lang === "es" ? "Todos" : "All"}
            </button>
            {months.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setSelectedMonth(m)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
                  selectedMonth === m
                    ? "text-white"
                    : "bg-white border border-black/[0.08] text-[#6e6e73] hover:border-[#161b3d]/30"
                }`}
                style={selectedMonth === m ? { backgroundColor: BRAND_NAVY } : undefined}
              >
                {formatMonth(m, lang)}
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-black/[0.04] p-5 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
                <div className="h-3 bg-gray-100 rounded w-1/2 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-100 rounded-2xl px-5 py-4 text-[13px] text-red-600">
            {error}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && visibleRegistrations.length === 0 && (
          <div className="bg-white rounded-2xl border border-black/[0.04] px-6 py-10 text-center">
            <p className="text-[15px] font-semibold text-[#1d1d1f] mb-1">{T.emptyTitle}</p>
            <p className="text-[13px] text-[#86868b] mb-5">{T.emptyBody}</p>
            <Link
              to="/"
              className="inline-block px-5 py-2.5 rounded-xl text-[13px] font-semibold text-white"
              style={{ backgroundColor: BRAND_MAROON }}
            >
              {T.goToForm}
            </Link>
          </div>
        )}

        {/* Registration cards */}
        {!loading && !error && visibleRegistrations.length > 0 && (
          <div className="space-y-3">
            {visibleRegistrations.map((reg) => (
              <div key={reg.id} className="bg-white rounded-2xl border border-black/[0.04] p-5 shadow-sm">
                {/* Header row */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-[15px] font-semibold text-[#1d1d1f]">
                      {reg.childName || T.student}
                    </p>
                    {reg.month && (
                      <p className="text-[12px] text-[#86868b] mt-0.5">{formatMonth(reg.month, lang)}</p>
                    )}
                  </div>
                  <StatusBadge status={reg.status} lang={lang} />
                </div>

                {/* Total + student code */}
                <div className="flex items-center justify-between text-[13px] text-[#6e6e73]">
                  <span>
                    {T.total}{" "}
                    <span className="font-semibold text-[#1d1d1f]">{formatCurrency(reg.totalPrice)}</span>
                  </span>
                  {reg.studentCode && (
                    <span className="text-[11px] font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">
                      #{reg.studentCode}
                    </span>
                  )}
                </div>

                {/* Inline class list */}
                <ClassList
                  classIds={reg.selectedClasses}
                  classMap={classMap}
                  paymentFrequency={reg.paymentFrequency}
                  lang={lang}
                />

                {/* Action buttons */}
                {canPay(reg.status) && reg.paymentMethod === "ONLINE" && (
                  <Link
                    to={`/pagos/${reg.studentCode || reg.id}`}
                    className="mt-4 block w-full text-center py-2.5 rounded-xl text-[13px] font-semibold text-white transition-opacity"
                    style={{ backgroundColor: BRAND_NAVY }}
                  >
                    {T.viewAndPay}
                  </Link>
                )}
                {canPay(reg.status) && reg.paymentMethod === "LOCAL" && (
                  <div className="mt-4 rounded-xl bg-blue-50 border border-blue-100 px-4 py-2.5 text-[12px] text-blue-700 text-center font-semibold">
                    {T.payAtSchool}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* New enrollment CTA */}
        {!loading && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleNewEnrollment}
              className="text-[12px] font-semibold text-[#8b1e22] hover:underline"
            >
              {T.newEnrollment}
            </button>
          </div>
        )}
      </main>
    </motion.div>
  );
}
